// src/pages/CommunicationsCenter.jsx
// State-of-the-art 3-Panel Communications Center
// Unified inbox for SMS, Email, and Calls in one place
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  MessageSquare, Mail, Phone, Search, RefreshCw, Plus, Inbox,
  AlertCircle, Loader2
} from 'lucide-react';

// Import sub-components
import ConversationList from '../components/communications/ConversationList';
import UnifiedThread from '../components/communications/UnifiedThread';
import ClientSidebar from '../components/communications/ClientSidebar';
import SMSOutcomeModal from '../components/SMSOutcomeModal';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function CommunicationsCenter() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // =========================================================================
  // STATE MANAGEMENT
  // =========================================================================

  // Conversation list state
  const [conversations, setConversations] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'unread', 'sms', 'email', 'call'
  const [searchQuery, setSearchQuery] = useState('');

  // Thread state
  const [thread, setThread] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);

  // Client details state
  const [selectedClient, setSelectedClient] = useState(null);

  // Unread counts
  const [unreadCounts, setUnreadCounts] = useState({ sms: 0, email: 0, call: 0, total: 0 });

  // Compose state
  const [composeMode, setComposeMode] = useState('sms'); // 'sms', 'email', 'call'

  // Audio state
  const audioRef = useRef(null);
  const [playingId, setPlayingId] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);

  // SMS outcome tracking
  const [hasSentSMS, setHasSentSMS] = useState(false);
  const [showOutcomeModal, setShowOutcomeModal] = useState(false);
  const [pendingClientSwitch, setPendingClientSwitch] = useState(null);

  // Mobile responsive
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [mobilePanel, setMobilePanel] = useState('list'); // 'list', 'thread', 'sidebar'

  // =========================================================================
  // RESPONSIVE HANDLER
  // =========================================================================

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // =========================================================================
  // DATA FETCHING
  // =========================================================================

  // Fetch conversation list (grouped by client)
  // When typeFilter is 'sms', 'email', or 'call', the backend returns the most recent
  // message OF THAT TYPE per client (not the most recent overall message).
  // This prevents clients from disappearing when their last message is a different type.
  const fetchConversations = useCallback(async (typeFilter) => {
    setLoading(true);
    setError(null);
    try {
      // Pass type to backend so it groups by the correct message type
      const typeParam = (typeFilter && ['sms', 'email', 'call'].includes(typeFilter))
        ? `?type=${typeFilter}` : '';
      const res = await fetch(`${API_BASE}/comms/grouped${typeParam}`, {
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to fetch conversations');

      const data = await res.json();
      setConversations(data.data || []);

      // Also fetch unread counts
      const unreadRes = await fetch(`${API_BASE}/comms/unread-counts`, {
        credentials: 'include'
      });

      if (unreadRes.ok) {
        const unreadData = await unreadRes.json();
        setUnreadCounts(unreadData.data || { sms: 0, email: 0, call: 0, total: 0 });
      }
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
      setError('Failed to load conversations. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch thread for selected client
  const fetchThread = useCallback(async (clientId) => {
    if (!clientId) return;

    setThreadLoading(true);
    try {
      // Fetch all communications for this client
      const res = await fetch(`${API_BASE}/comms?clientId=${clientId}&limit=100`, {
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to fetch thread');

      const data = await res.json();
      // Sort by date ascending (oldest first) — like a phone: newest at bottom
      const sortedThread = (data.data || []).sort(
        (a, b) => new Date(a.timestamp || a.createdAt) - new Date(b.timestamp || b.createdAt)
      );
      setThread(sortedThread);

      // Fetch client details
      const clientRes = await fetch(`${API_BASE}/clients/${clientId}`, {
        credentials: 'include'
      });

      if (clientRes.ok) {
        const clientData = await clientRes.json();
        setSelectedClient(clientData.data || clientData);
      }

      // NOTE: Do NOT auto-mark as read when opening a thread.
      // Messages are marked read only when agent clicks "Mark Read" or responds.

      // Refresh unread counts
      const unreadRes = await fetch(`${API_BASE}/comms/unread-counts`, {
        credentials: 'include'
      });
      if (unreadRes.ok) {
        const unreadData = await unreadRes.json();
        setUnreadCounts(unreadData.data || { sms: 0, email: 0, call: 0, total: 0 });
      }
    } catch (err) {
      console.error('Failed to fetch thread:', err);
    } finally {
      setThreadLoading(false);
    }
  }, []);

  // Initial load + re-fetch when filter changes
  useEffect(() => {
    fetchConversations(activeFilter);
  }, [fetchConversations, activeFilter]);

  // Deep link: auto-select client from URL param (?clientId=xxx)
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (loading || deepLinkHandled.current) return;
    const clientIdParam = searchParams.get('clientId');
    if (clientIdParam) {
      deepLinkHandled.current = true;
      setSelectedClientId(clientIdParam);
      fetchThread(clientIdParam);
      setSearchParams({}, { replace: true });
    }
  }, [loading, searchParams, setSearchParams, fetchThread]);

  // =========================================================================
  // HANDLERS
  // =========================================================================

  // Actually perform the client switch
  const doSelectClient = useCallback((clientId) => {
    setSelectedClientId(clientId);
    setHasSentSMS(false);
    fetchThread(clientId);
    if (isMobile) {
      setMobilePanel('thread');
    }
    // Auto-mark as read when opening a conversation
    fetch(`${API_BASE}/comms/mark-read/${clientId}/all`, {
      method: 'POST', credentials: 'include'
    }).then(async (r) => {
      if (!r.ok) {
        console.error('[Comms] Auto mark-read failed:', r.status);
        return;
      }
      const data = await r.json();
      console.log(`[Comms] Auto mark-read: ${data.markedRead} marked, ${data.remainingUnread} remaining for client ${clientId}`);
      setConversations(prev => prev.map(c =>
        c.clientId === clientId ? { ...c, unreadCount: 0 } : c
      ));
      fetch(`${API_BASE}/comms/unread-counts`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(d => d && setUnreadCounts(d.data || { sms: 0, email: 0, call: 0, total: 0 }));
    }).catch((err) => { console.error('[Comms] Auto mark-read error:', err); });
  }, [fetchThread, isMobile]);

  // Select a client conversation (intercepts if SMS outcome needs logging)
  const handleSelectClient = useCallback((clientId) => {
    // If agent sent SMS in current conversation, prompt for outcome before switching
    if (hasSentSMS && selectedClientId && clientId !== selectedClientId) {
      setPendingClientSwitch(clientId);
      setShowOutcomeModal(true);
      return;
    }
    doSelectClient(clientId);
  }, [hasSentSMS, selectedClientId, doSelectClient]);

  // Mark all inbound messages as read for the selected client
  const handleMarkRead = useCallback(async () => {
    if (!selectedClientId) return;
    try {
      // Mark all types (sms, email, call) in a single request
      const markRes = await fetch(`${API_BASE}/comms/mark-read/${selectedClientId}/all`, {
        method: 'POST', credentials: 'include'
      });
      if (!markRes.ok) {
        const errData = await markRes.json().catch(() => ({}));
        console.error('[Comms] Mark-read FAILED:', markRes.status, errData);
        return;
      }
      const markData = await markRes.json();
      console.log(`[Comms] Mark-read OK: ${markData.markedRead} marked, ${markData.remainingUnread} still unread for client ${selectedClientId}`);

      // Update local state immediately for responsiveness
      setConversations(prev => prev.map(c =>
        c.clientId === selectedClientId ? { ...c, unreadCount: 0 } : c
      ));

      // Re-fetch conversations from server to verify DB persistence
      fetchConversations(activeFilter);
    } catch (err) {
      console.error('[Comms] Mark-read error:', err);
    }
  }, [selectedClientId, fetchConversations, activeFilter]);

  // Send message
  const handleSendMessage = useCallback(async (type, content, metadata = {}) => {
    if (!selectedClientId) return;

    try {
      const payload = {
        clientId: selectedClientId,
        type,
        direction: 'outbound',
        body: content,
        ...metadata
      };

      const res = await fetch(`${API_BASE}/comms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to send message');
      }

      const data = await res.json();

      // Optimistically add message to thread
      const newMessage = {
        id: data.data?.id || Date.now(),
        type,
        direction: 'outbound',
        body: content,
        timestamp: new Date().toISOString(),
        client: selectedClient,
        ...metadata
      };

      setThread(prev => [...prev, newMessage]);

      // Update conversation list
      setConversations(prev => prev.map(c =>
        c.clientId === selectedClientId
          ? { ...c, lastMessage: newMessage, lastMessageTime: new Date().toISOString() }
          : c
      ));

      // Track that agent sent an SMS for outcome prompt
      if (type === 'sms') {
        setHasSentSMS(true);
      }

      // Mark all inbound messages as read since agent responded
      fetch(`${API_BASE}/comms/mark-read/${selectedClientId}/all`, {
        method: 'POST', credentials: 'include'
      }).then(async (r) => {
        if (!r.ok) { console.error('[Comms] Post-send mark-read failed:', r.status); return; }
        const data = await r.json();
        console.log(`[Comms] Post-send mark-read: ${data.markedRead} marked, ${data.remainingUnread} remaining`);
        setConversations(prev => prev.map(c =>
          c.clientId === selectedClientId ? { ...c, unreadCount: 0 } : c
        ));
        fetch(`${API_BASE}/comms/unread-counts`, { credentials: 'include' })
          .then(r => r.ok ? r.json() : null)
          .then(d => d && setUnreadCounts(d.data || { sms: 0, email: 0, call: 0, total: 0 }));
      }).catch(err => console.error('[Comms] Post-send mark-read error:', err));

      // Notify sidebar to refresh risk score (backend triggers Lambda rescore on POST /comms)
      window.dispatchEvent(new CustomEvent('risk-score-updated', { detail: { clientId: selectedClientId } }));

      return { success: true, data: data.data };
    } catch (err) {
      console.error('Failed to send message:', err);
      return { success: false, error: err.message };
    }
  }, [selectedClientId, selectedClient]);

  // Play audio (recording or voicemail)
  const handlePlayAudio = useCallback(async (type, id) => {
    if (!audioRef.current) return;

    // If already playing this audio, pause it
    if (playingId === id) {
      audioRef.current.pause();
      setPlayingId(null);
      return;
    }

    setAudioLoading(true);
    try {
      const endpoint = type === 'voicemail'
        ? `${API_BASE}/calls/voicemail/${id}`
        : `${API_BASE}/calls/recording/${id}`;

      const response = await fetch(endpoint, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch audio');

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Clean up previous blob URL
      if (audioRef.current.src.startsWith('blob:')) {
        URL.revokeObjectURL(audioRef.current.src);
      }

      audioRef.current.src = blobUrl;
      setPlayingId(id);
      await audioRef.current.play();
    } catch (err) {
      console.error('Error playing audio:', err);
      setPlayingId(null);
    } finally {
      setAudioLoading(false);
    }
  }, [playingId]);

  // Refresh all data
  const handleRefresh = useCallback(() => {
    fetchConversations(activeFilter);
    if (selectedClientId) {
      fetchThread(selectedClientId);
    }
  }, [fetchConversations, fetchThread, selectedClientId, activeFilter]);

  // Refresh client data (called when risk score updates)
  const handleRefreshClient = useCallback(async (clientId) => {
    try {
      const clientRes = await fetch(`${API_BASE}/clients/${clientId}`, {
        credentials: 'include'
      });
      if (clientRes.ok) {
        const clientData = await clientRes.json();
        setSelectedClient(clientData.data || clientData);
      }
    } catch (err) {
      console.error('Failed to refresh client:', err);
    }
  }, []);

  // Navigate to client profile
  const handleNavigateToProfile = useCallback(() => {
    if (selectedClientId) {
      navigate(`/clients/${selectedClientId}`);
    }
  }, [navigate, selectedClientId]);

  // Handle outcome modal close (skip/save both proceed to pending switch)
  const handleOutcomeComplete = useCallback(() => {
    setShowOutcomeModal(false);
    setHasSentSMS(false);
    if (pendingClientSwitch) {
      doSelectClient(pendingClientSwitch);
      setPendingClientSwitch(null);
    }
  }, [pendingClientSwitch, doSelectClient]);

  // Back to list (mobile)
  const handleBackToList = useCallback(() => {
    setMobilePanel('list');
  }, []);

  // =========================================================================
  // FILTERED CONVERSATIONS
  // =========================================================================

  const filteredConversations = useMemo(() => {
    let filtered = [...conversations];

    // Type filtering (sms/email/call) is now handled server-side via ?type= param.
    // Only apply client-side filters for 'unread' and search.
    if (activeFilter === 'unread') {
      filtered = filtered.filter(c => c.unreadCount > 0);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c =>
        (c.clientName || '').toLowerCase().includes(query) ||
        (c.lastMessage?.body || '').toLowerCase().includes(query)
      );
    }

    // Sort: unread first, then by most recent
    filtered.sort((a, b) => {
      // Unread first
      if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
      if (b.unreadCount > 0 && a.unreadCount === 0) return 1;
      // Then by most recent
      const aTime = new Date(a.lastMessage?.timestamp || a.lastMessageTime || 0);
      const bTime = new Date(b.lastMessage?.timestamp || b.lastMessageTime || 0);
      return bTime - aTime;
    });

    return filtered;
  }, [conversations, activeFilter, searchQuery]);

  // =========================================================================
  // RENDER
  // =========================================================================

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col bg-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Communications Center</h1>
          <p className="text-sm text-slate-500">Unified inbox for all client communications</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="mx-4 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
          <button
            onClick={handleRefresh}
            className="ml-auto text-sm text-red-600 hover:underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Main 3-Panel Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Conversation List */}
        {(!isMobile || mobilePanel === 'list') && (
          <ConversationList
            conversations={filteredConversations}
            selectedClientId={selectedClientId}
            onSelect={handleSelectClient}
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            unreadCounts={unreadCounts}
            loading={loading}
            isMobile={isMobile}
          />
        )}

        {/* Center Panel - Thread */}
        {(!isMobile || mobilePanel === 'thread') && (
          <UnifiedThread
            thread={thread}
            client={selectedClient}
            loading={threadLoading}
            onSendMessage={handleSendMessage}
            composeMode={composeMode}
            onComposeModeChange={setComposeMode}
            playingId={playingId}
            audioLoading={audioLoading}
            onPlayAudio={handlePlayAudio}
            isMobile={isMobile}
            onBack={handleBackToList}
            selectedClientId={selectedClientId}
            hasUnread={conversations.find(c => c.clientId === selectedClientId)?.unreadCount > 0}
            onMarkRead={handleMarkRead}
            onLogOutcome={() => setShowOutcomeModal(true)}
          />
        )}

        {/* Right Panel - Client Sidebar */}
        {selectedClient && !isMobile && (
          <ClientSidebar
            client={selectedClient}
            onNavigateToProfile={handleNavigateToProfile}
            onComposeChange={setComposeMode}
            onRefreshClient={handleRefreshClient}
          />
        )}
      </div>

      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        onEnded={() => setPlayingId(null)}
        onPause={() => setPlayingId(null)}
        className="hidden"
      />

      {/* SMS Outcome Modal - shown when agent leaves a conversation after sending SMS */}
      <SMSOutcomeModal
        isOpen={showOutcomeModal}
        onClose={handleOutcomeComplete}
        client={selectedClient}
        onSave={handleOutcomeComplete}
      />
    </div>
  );
}
